import { isEmpty, lowerCase, orderBy } from 'lodash';
import type { HotelResultsPolicyHotelFields, HotelResultsTier } from 'src/types/hotelResultsPolicy.type';
import { hotelDistanceSortKey } from './distance.util';
import { driveTimeSortKey } from './driveTime.util';
import { processFacilities } from './hotelFilter';
import { multiBedFromBedTypes } from './plannerRooms.util';
import { getItemPerRoomNightlyRate } from './pricing.util';

/**
 * NPT-2022 / NPT-2023 — the one ordering and filtering utility the web owns (ADR §5, §6, §7):
 * https://milemarker.atlassian.net/wiki/spaces/NT/pages/1374486530
 *
 * The API scores and orders every list (`recommendedScore`, `isUnrated`, `designationTier`,
 * `pinned`, ADR §8). The web never recomputes the score: `orderHotels` re-sorts on the returned
 * fields when the traveler picks another sort, puts unrated hotels last under every sort and
 * keeps the pinned tiers on top (`applyPins`). `filterHotels` applies the traveler's own filters
 * and nothing else: no organization floor lives here or anywhere downstream of it.
 *
 * The legacy fields (`designation_category`, `isTeamHotel`, `isOfficialSponsor`, ...) are read as
 * a fallback for one release, for responses that predate the policy fields.
 */

/** The tiers that stay on top under every sort. Mirrors the API's `pinnedTiers`; read it from the entry's `pinned` first. */
export const PINNED_TIERS: readonly HotelResultsTier[] = ['TEAM', 'SPONSOR'];

/** Designation rank under the default sort (ADR §5.1). Mirrors the API's `tierOrder`; pass the payload's when you have it. */
export const TIER_ORDER: readonly HotelResultsTier[] = ['TEAM', 'SPONSOR', 'LUXURY', 'BUDGET'];

type LegacyDesignationCategory = 'TEAM' | 'BUDGET' | 'LUXURY';

/** What a list item can carry: the ADR §8 fields, the legacy per-game category, and the legacy card flags. */
export type TieredHotelLike = Partial<Pick<HotelResultsPolicyHotelFields, 'designationTier' | 'pinned' | 'isPreferred'>> & {
   hotel?: { designation_category?: LegacyDesignationCategory | null } | null;
   isTeamHotel?: boolean;
   isOfficialSponsor?: boolean;
   isLuxuryPick?: boolean;
   isBudgetPick?: boolean;
   isPreferredHotel?: boolean;
};

/** The tier the legacy fields imply, in the ADR §5 precedence (TEAM beats SPONSOR for the same hotel). */
const legacyTierOf = (item: TieredHotelLike | null | undefined): HotelResultsTier | null => {
   if (!item) return null;
   const category = item.hotel?.designation_category ?? null;
   if (item.isTeamHotel || category === 'TEAM') return 'TEAM';
   if (item.isOfficialSponsor) return 'SPONSOR';
   if (item.isLuxuryPick || category === 'LUXURY') return 'LUXURY';
   if (item.isBudgetPick || category === 'BUDGET') return 'BUDGET';
   return null;
};

/** The hotel's designation tier: the API's `designationTier`, else what the legacy fields imply. */
export const designationTierOf = (item: TieredHotelLike | null | undefined): HotelResultsTier | null => item?.designationTier ?? legacyTierOf(item);

/** True for a hotel that stays on top under every sort: the API's `pinned`, else a legacy TEAM / sponsor. */
export const isPinnedHotel = (item: TieredHotelLike | null | undefined): boolean => {
   if (item?.pinned === true) return true;
   const tier = designationTierOf(item);
   return tier != null && PINNED_TIERS.includes(tier);
};

/** The card flags every surface renders from, derived from one tier so no card can disagree with the order. */
export const tierFlagsOf = (tier: HotelResultsTier | null | undefined) => ({
   isTeamHotel: tier === 'TEAM',
   isOfficialSponsor: tier === 'SPONSOR',
   isLuxuryPick: tier === 'LUXURY',
   isBudgetPick: tier === 'BUDGET',
});

/** What a card or a filter can read bedding from: the API's `multiBed` (ADR §8), else the static catalogue. */
export type MultiBedLike = { multiBed?: boolean | null; hotel?: { bed_types?: unknown } | null } | null | undefined;

/**
 * NPT-2026 — the one bedding answer every card and the "Two beds" filter read (ADR §3, §7, §8).
 *
 * The API attaches `multiBed: true | false | null` to every policy-scored entry; that wins
 * whenever the field is present (null included — the API read the same catalogue). An entry
 * without the field (a cache entry from before the policy, a static row the API did not
 * score) falls back to parsing `hotel.bed_types` with the same rules. Null is unknown.
 */
export function multiBedOf(entry: MultiBedLike): boolean | null {
   if (!entry) return null;
   if (entry.multiBed !== undefined) return entry.multiBed ?? null;
   return multiBedFromBedTypes(entry.hotel?.bed_types);
}

/**
 * The traveler's "Two beds" filter (ADR §7): off by default for every organization type and
 * never an org rule. On, it keeps only the hotels KNOWN to have a room with two beds or a
 * bed plus a sofa bed — exactly the hotels that carry the badge, so the filtered list and
 * the badges agree. Unknown bedding is kept while the filter is off and dropped while it is on.
 */
export function passesTwoBedsFilter(entry: MultiBedLike, twoBedsOn: boolean): boolean {
   if (!twoBedsOn) return true;
   return multiBedOf(entry) === true;
}

/**
 * Pins on top of a sort (ADR §5.4).
 *
 * `hotels` is the list in API order and decides which hotels pin and in what order; `sortedRest`
 * is the same set in the active sort's order. Pinned hotels lead, TEAM before SPONSOR (`tierOf`,
 * the item's tier by default; pass one when the list holds wrappers), then in API order within a
 * tier; every other hotel follows in `sortedRest`'s order. With nothing pinned the result is `sortedRest` itself, so a
 * plain search costs nothing. Items are matched by reference: sort with something that keeps
 * the objects (lodash `orderBy`, `Array.prototype.sort`), not something that clones them.
 */
export function applyPins<T>(
   hotels: readonly T[],
   sortedRest: T[],
   isPinned: (hotel: T) => boolean = isPinnedHotel as (hotel: T) => boolean,
   tierOf: (hotel: T) => HotelResultsTier | null = designationTierOf as (hotel: T) => HotelResultsTier | null,
): T[] {
   const pinRank = (hotel: T) => {
      const rank = PINNED_TIERS.indexOf(tierOf(hotel) as HotelResultsTier);
      return rank === -1 ? PINNED_TIERS.length : rank;
   };
   // The API already sends TEAM before SPONSOR; a legacy response (team id from the game, sponsor
   // id from the org) arrives in list order, so the pins are ranked by tier here too, stably.
   const pinned = (hotels ?? [])
      .map((hotel, index) => ({ hotel, index }))
      .filter(({ hotel }) => isPinned(hotel))
      .sort((a, b) => pinRank(a.hotel) - pinRank(b.hotel) || a.index - b.index)
      .map(({ hotel }) => hotel);
   if (!pinned.length) return sortedRest;
   const pinnedSet = new Set(pinned);
   return [...pinned, ...(sortedRest ?? []).filter(hotel => !pinnedSet.has(hotel))];
}

// ─── NPT-2023: ordering ──────────────────────────────────────────────────────────────────────

/**
 * The traveler sorts. `recommended` is the default on every surface (ADR §5). `drive_time` is
 * the search page's extra option when the serp routed drive times (NPT-2175); it is listed here
 * so that page needs no sort of its own.
 */
export const HOTEL_SORTS = ['recommended', 'distance', 'price_asc', 'price_desc', 'rating', 'drive_time'] as const;
export type HotelSort = (typeof HOTEL_SORTS)[number];
export const DEFAULT_HOTEL_SORT: HotelSort = 'recommended';

export const isHotelSort = (value: unknown): value is HotelSort => HOTEL_SORTS.includes(value as HotelSort);

/** What `orderHotels` reads from a list item. Every field is optional: a pre-policy response still sorts. */
export type OrderableHotelLike = TieredHotelLike & {
   isUnrated?: boolean | null;
   recommendedScore?: number | null;
   hotel?: {
      designation_category?: LegacyDesignationCategory | null;
      star_rating?: number | string | null;
      distance_miles?: number | string | null;
      driveTimeSeconds?: number | null;
   } | null;
   rating?: { rating?: number | string | null; reviews?: number | string | null } | null;
};

/** The shape `getItemPerRoomNightlyRate` reads; the list items carry far more, so callers narrow to it. */
type PricedItem = Parameters<typeof getItemPerRoomNightlyRate>[0];

/**
 * Unrated (ADR §5.3): the API's `isUnrated` when the entry carries it, else no star class or a
 * star class of 0. Ranked after every rated hotel under every sort and labelled "Unrated".
 */
export const isUnratedHotel = (item: OrderableHotelLike | null | undefined): boolean => {
   if (!item) return false;
   if (typeof item.isUnrated === 'boolean') return item.isUnrated;
   return !(Number(item.hotel?.star_rating) > 0);
};

/**
 * The API's Recommended score (ADR §4), never recomputed here. A missing score and the API's
 * `-Infinity` (an unplaceable hotel) both read as -Infinity, so they sort last among rated hotels
 * and keep their API order between themselves.
 */
export const recommendedScoreOf = (item: OrderableHotelLike | null | undefined): number => {
   const score = item?.recommendedScore;
   return typeof score === 'number' && !Number.isNaN(score) ? score : -Infinity;
};

/** Mean guest rating over hotels that have one; 4.0 when none do. The prior for the Guest rating sort. */
export const calculateMeanRating = (hotels: readonly OrderableHotelLike[]): number => {
   const rated = (hotels || []).filter(h => Number(h?.rating?.rating) > 0);
   if (rated.length === 0) return 4.0;
   return rated.reduce((acc, h) => acc + Number(h?.rating?.rating || 0), 0) / rated.length;
};

/** Bayesian weighted rating: (v/(v+m))·R + (m/(v+m))·C. Callers cap v as needed. */
export const calculateBayesianRating = (rating: number, reviews: number, meanRating: number, m: number): number => {
   const v = reviews;
   return (v / (v + m)) * rating + (m / (v + m)) * meanRating;
};

/**
 * The Guest rating sort's prior weight: a hotel with few reviews is pulled towards the list's mean
 * so a single 5.0 cannot lead. Not a scoring formula (ADR §4); the same stabiliser the "Best
 * rating" sort has always used.
 */
export const GUEST_RATING_PRIOR_WEIGHT = 50;

const guestRatingSortKey = (item: OrderableHotelLike, meanRating: number): number =>
   calculateBayesianRating(Number(item?.rating?.rating || 0), Number(item?.rating?.reviews || 0), meanRating, GUEST_RATING_PRIOR_WEIGHT);

/** The displayed per-room nightly price, so the price sorts match the number on the card; null when unpriced. */
const priceSortKey = (item: OrderableHotelLike, rooms?: number | string | null): number | null =>
   getItemPerRoomNightlyRate(item as PricedItem, rooms);

const byPrice = <T extends OrderableHotelLike>(hotels: readonly T[], direction: 1 | -1, rooms?: number | string | null): T[] =>
   [...hotels].sort((a, b) => {
      const ra = priceSortKey(a, rooms);
      const rb = priceSortKey(b, rooms);
      if (ra == null && rb == null) return 0;
      // Unpriced hotels (sold out, still loading) go last whichever way the prices run.
      if (ra == null) return 1;
      if (rb == null) return -1;
      return (ra - rb) * direction;
   });

export type OrderHotelsOptions = {
   /** The room count the search priced at; the price sorts compare per-room prices (NPT-1718). */
   rooms?: number | string | null;
   /** The policy's `tierOrder` (ADR §8); defaults to the platform order. */
   tierOrder?: readonly HotelResultsTier[];
   /**
    * The caller already applied the traveler's sort (the search page passes its own ordered list to
    * `rankGameHotels`): keep that order and apply only the unrated-last rule and the pins.
    */
   presorted?: boolean;
};

/** 0 for TEAM … 3 for BUDGET, `tierOrder.length` for an undesignated hotel. */
const tierRankOf = (item: OrderableHotelLike, tierOrder: readonly HotelResultsTier[]): number => {
   const tier = designationTierOf(item);
   const rank = tier == null ? -1 : tierOrder.indexOf(tier);
   return rank < 0 ? tierOrder.length : rank;
};

/**
 * Orders a list for a traveler surface (ADR §5):
 *
 * - `recommended` (the default): designation rank first — TEAM, SPONSOR, LUXURY, BUDGET, then
 *   everything else — and within a rank rated hotels before unrated, by the API's
 *   `recommendedScore` (highest first). This is the order the API returns, rebuilt for lists that
 *   lost it (a client-side filter, a pre-policy response decorated with legacy tiers).
 * - any other sort: the chosen key — `distance` and `drive_time` nearest first, the price sorts on
 *   the displayed per-room price, `rating` on the review-count-weighted guest rating — then unrated
 *   hotels after every rated hotel, then the pinned tiers (TEAM, SPONSOR) back on top in API order.
 *   LUXURY and BUDGET follow the traveler's sort with the rest (ADR §5.4).
 *
 * Every step keeps the objects (no clones), so `applyPins` can match the pins by reference, and
 * every sort is stable, so hotels the sort cannot tell apart keep the API's order.
 */
export function orderHotels<T extends OrderableHotelLike>(
   hotels: readonly T[],
   sort: HotelSort = DEFAULT_HOTEL_SORT,
   options: OrderHotelsOptions = {},
): T[] {
   const list = hotels ?? [];
   if (!list.length) return [];
   const tierOrder = options.tierOrder ?? TIER_ORDER;

   if (!options.presorted && sort === 'recommended') {
      const byRank = orderBy(
         list,
         [item => tierRankOf(item, tierOrder), item => (isUnratedHotel(item) ? 1 : 0), recommendedScoreOf],
         ['asc', 'asc', 'desc'],
      );
      // Legacy card flags can mark a hotel pinned without a tier; the pins pass keeps those on top too
      // and ranks the pinned block itself (TEAM before SPONSOR, then API order).
      return applyPins(list, byRank);
   }

   let sorted: T[];
   switch (options.presorted ? 'presorted' : sort) {
      case 'presorted':
         sorted = [...list];
         break;
      case 'distance':
         sorted = orderBy(list, [hotelDistanceSortKey], ['asc']);
         break;
      case 'drive_time':
         // A hotel without a routed time is ordered by an estimate (never shown); see driveTimeSortKey.
         sorted = orderBy(list, [item => driveTimeSortKey(item)], ['asc']);
         break;
      case 'price_asc':
         sorted = byPrice(list, 1, options.rooms);
         break;
      case 'price_desc':
         sorted = byPrice(list, -1, options.rooms);
         break;
      case 'rating': {
         const meanRating = calculateMeanRating(list);
         sorted = orderBy(list, [item => guestRatingSortKey(item, meanRating)], ['desc']);
         break;
      }
      default:
         sorted = [...list];
         break;
   }

   const unratedLast = [...sorted.filter(item => !isUnratedHotel(item)), ...sorted.filter(isUnratedHotel)];
   return applyPins(list, unratedLast);
}

// ─── NPT-2023: traveler filters ──────────────────────────────────────────────────────────────

/**
 * The traveler's own filters (ADR §7), as the search URL carries them. Nothing here is an
 * organization rule: the org's floors are weights inside the API's score, never a filter.
 */
export type TravelerFilters = {
   /** Star classes the traveler picked (exact match on the integer class). Absent or empty = no star filter. */
   stars?: ReadonlyArray<string | number> | null;
   chains?: ReadonlyArray<string> | null;
   amenities?: ReadonlyArray<string> | null;
   refunds?: ReadonlyArray<string> | null;
   /** The first value is the minimum guest rating. */
   guestRating?: ReadonlyArray<string | number> | null;
   propertyTypes?: ReadonlyArray<string> | null;
   budgetMin?: string | number | null;
   budgetMax?: string | number | null;
   /** NPT-1721 legacy slider params; read only when `budgetMin`/`budgetMax` are absent. */
   minAmount?: string | number | null;
   maxAmount?: string | number | null;
   /** Hotel name or chain contains this. */
   searchText?: string | null;
   /** The room count the search priced at; the price range compares per-room prices (NPT-1718). */
   rooms?: number | string | null;
   /** `beds=2` turns the "Two beds" filter on (NPT-2026). */
   beds?: number | string | null;
   favoritesOnly?: boolean | 'true' | 'false' | null;
   /** The traveler's favorite hotel ids; `favoritesOnly` keeps only these. */
   favoriteHotelIds?: ReadonlyArray<string | number> | null;
};

/** What `filterHotels` reads from a list item. */
export type FilterableHotelLike = OrderableHotelLike &
   MultiBedLike & {
      id?: string | number | null;
      hotel?: {
         id?: string | number | null;
         star_rating?: number | string | null;
         hotel_chain?: string | null;
         name?: string | null;
         kind?: string | null;
         facilities?: string[] | null;
         bed_types?: unknown;
         designation_category?: LegacyDesignationCategory | null;
      } | null;
      rates?: Array<{ freeCancellation?: boolean | null } | null | undefined> | null;
      priceStatus?: string | null;
   };

/** The legacy `?minAmount=&maxAmount=` slider's "and up" stop, which means no ceiling (NPT-1721). */
const LEGACY_AMOUNT_SLIDER_MAX = 500;

const present = (value: unknown): boolean => value !== undefined && value !== null && value !== '';

/** The price range the traveler set: `budgetMin`/`budgetMax`, else the legacy slider params. */
const budgetOf = (filters: TravelerFilters): { min: number; max: number } => {
   if (present(filters.budgetMin) || present(filters.budgetMax)) {
      return { min: Number(filters.budgetMin) || 0, max: Number(filters.budgetMax) || 0 };
   }
   const min = Number(filters.minAmount);
   const max = Number(filters.maxAmount);
   return { min: min > 0 ? min : 0, max: max > 0 && max < LEGACY_AMOUNT_SLIDER_MAX ? max : 0 };
};

/** "Hotel, Hotel" → "hotel" — a kind string can repeat itself across the catalogue's sources. */
const normalizeKind = (kind: string | null | undefined): string =>
   lowerCase(
      (kind ?? '')
         .split(',')
         .map(s => s.trim())
         .filter((s, idx, arr) => arr.indexOf(s) === idx)
         .join(', '),
   );

const idOf = (item: FilterableHotelLike): string => String(item?.hotel?.id ?? item?.id ?? '');

/**
 * Applies the traveler's filters (ADR §7) on top of the policy order. Returns the same objects
 * (no clones) so the result can go straight into `orderHotels` / `applyPins`.
 *
 * The star filter is the ONLY filter that removes an unrated hotel: a traveler who asks for
 * 4-star hotels does not want "unknown". Every other filter judges what it can see and keeps
 * a hotel whose value is unknown (no facilities, no rating), as before.
 */
export function filterHotels<T extends FilterableHotelLike>(hotels: readonly T[], filters: TravelerFilters | null | undefined): T[] {
   const list = hotels ?? [];
   if (!filters || isEmpty(filters)) return [...list];

   const stars = (filters.stars ?? []).map(s => Number(s)).filter(s => Number.isFinite(s));
   const chains = filters.chains ?? [];
   const amenities = filters.amenities ?? [];
   const refunds = filters.refunds ?? [];
   const propertyTypes = filters.propertyTypes ?? [];
   const minGuestRating = filters.guestRating?.length ? Number(filters.guestRating[0]) : null;
   const budget = budgetOf(filters);
   const searchText = lowerCase(filters.searchText ?? '');
   const roomCount = Number(filters.rooms) > 0 ? Number(filters.rooms) : 1;
   const twoBedsOn = Number(filters.beds) > 0;
   const favoritesOnly = filters.favoritesOnly === true || filters.favoritesOnly === 'true';
   const favoriteIds = new Set((filters.favoriteHotelIds ?? []).map(String));

   const passesStars = (item: T): boolean => {
      if (!stars.length) return true;
      // Exact-or-above is what the chips mean once several are picked; an unrated hotel matches none.
      if (isUnratedHotel(item)) return false;
      return stars.includes(Number(item.hotel?.star_rating));
   };

   const passesFacilities = (facilities: string[] | null | undefined): boolean => {
      if (!amenities.length || !facilities?.length) return true;
      const mapped = processFacilities(facilities)?.map(f => f.display) || [];
      return amenities.every(f => mapped.includes(f));
   };

   const passesRefunds = (item: T): boolean => {
      if (!refunds.includes('refundableRatesVailable')) return true;
      if (item.priceStatus === 'loading') return true;
      // NPT-1721: a hotel can arrive with `rates: null`; it is excluded below, but this line must not assume an array.
      return Array.isArray(item.rates) && item.rates.some(r => !!r?.freeCancellation);
   };

   const passesGuestRating = (item: T): boolean => {
      if (minGuestRating == null || Number.isNaN(minGuestRating)) return true;
      const rating = Number(item.rating?.rating);
      if (Number.isNaN(rating)) return true;
      return rating >= minGuestRating;
   };

   const passesBudget = (item: T): boolean => {
      if (item.priceStatus === 'loading' || (!budget.min && !budget.max)) return true;
      // The price the traveler SEES for one room per night (discounted when the org passes a traveler share), the
      // same helper the result cards, map pins and histogram use, so the filter matches the label.
      const nightly = getItemPerRoomNightlyRate(item as PricedItem, roomCount);
      return nightly != null && (!budget.min || nightly >= budget.min) && (!budget.max || nightly < budget.max);
   };

   const passesText = (item: T): boolean => {
      if (!searchText) return true;
      return lowerCase(item.hotel?.hotel_chain ?? '').includes(searchText) || lowerCase(item.hotel?.name ?? '').includes(searchText);
   };

   return list.filter(item => {
      const hotel = item.hotel ?? {};
      return (
         passesStars(item) &&
         passesFacilities(hotel.facilities) &&
         passesRefunds(item) &&
         // No priceStatus guard: bedding is static, so this answers the same whether or not prices have landed.
         passesTwoBedsFilter(item, twoBedsOn) &&
         passesGuestRating(item) &&
         passesBudget(item) &&
         (!chains.length || chains.some(c => lowerCase(hotel.hotel_chain?.trim() || '') === lowerCase(c?.trim() || ''))) &&
         (!propertyTypes.length || propertyTypes.some(pt => normalizeKind(hotel.kind) === lowerCase(pt?.trim() || ''))) &&
         passesText(item) &&
         (!favoritesOnly || favoriteIds.has(idOf(item))) &&
         item.rates !== null
      );
   });
}
